/**
 * View transitions, where they say something.
 *
 * The browser can animate between two DOM states if it is told which change to
 * capture. That is worth doing exactly where the animation carries meaning
 * that the two still frames do not:
 *
 *   - moving between views, so Back reads as going back rather than as the
 *     page having been replaced;
 *   - moving through the discovery queue, where the direction is the whole
 *     point of the gesture;
 *   - a row leaving a list, where watching the gap close is what tells you
 *     which row went.
 *
 * And not anywhere else. A feed page appending forty stories, a toast, a
 * dialog — those either have their own motion or are better without any.
 *
 * Two escapes, both checked at call time rather than at import: a reader can
 * turn reduced motion on halfway through a session, and the answer has to
 * change with them.
 */

import { flushSync } from "react-dom";

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const canTransition = () =>
  typeof document !== "undefined" &&
  typeof document.startViewTransition === "function" &&
  !reducedMotion();

/**
 * Run `update` as a view transition, or just run it.
 *
 * `flushSync` is the load-bearing part. The browser captures the "after" state
 * when the callback returns, and React's own updates are asynchronous — so
 * without it the callback returns before anything has changed and the browser
 * animates one frame into an identical one. Safe here because every caller is
 * an event handler; it would be a warning during render.
 */
/* Which transition last set the direction.
 *
 * `data-transition` is one attribute on the root, but transitions overlap: a
 * second one aborts the first, and the first's cleanup then runs *while the
 * second is still animating*. Without a claim check it deleted the direction
 * belonging to the transition that had replaced it, so pressing Back and then
 * Forward quickly animated the second one with no direction at all. */
let latest = 0;

export function withTransition(update, { direction } = {}) {
  if (!canTransition()) {
    update();
    return;
  }

  // Read by the `::view-transition-*` rules, so forward and back can move the
  // opposite way without two sets of transition names.
  const mine = ++latest;
  if (direction) document.documentElement.dataset.transition = direction;

  const clear = () => {
    if (direction && latest === mine) delete document.documentElement.dataset.transition;
  };

  // `then(clear, clear)` rather than `finally`: `finished` rejects when the
  // update callback throws, and `finally` would pass that rejection on to a
  // promise nobody holds — an unhandled rejection on top of whatever actually
  // went wrong. Here the cleanup runs either way and the failure is left to
  // the code that caused it.
  const running = document.startViewTransition(() => flushSync(update));
  running.finished.then(clear, clear);
  // `ready` rejects whenever the transition is skipped — a hidden tab, or a
  // newer transition replacing this one. Both are ordinary here and neither is
  // anybody's problem, but an unhandled rejection is a console error, and this
  // app's browser suite fails on those.
  running.ready?.catch(() => {});
}

/**
 * A `view-transition-name` for one item in a list.
 *
 * The value is a CSS custom-ident, so a compose service called `web.api` or a
 * volume called `2fa` cannot be used as one directly: dots are invalid and a
 * leading digit is invalid. The prefix also keeps a service and a volume of
 * the same name from claiming one name — two elements sharing a name aborts
 * the whole transition rather than degrading.
 */
export function transitionName(prefix, value) {
  return `${prefix}-${String(value).replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}
